import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Award, Filter, X, ChevronDown, Star, Heart, Users, ThumbsUp, Settings, CheckCircle, Dice1, RefreshCw, Clock, PlusCircle } from 'lucide-react';
import { GameCard } from '../components/GameCard';
import { getRankedGames, toggleOwned, toggleWishlist, togglePlayed } from '../lib/game';
import { useAuth } from '../contexts/AuthContext';
import type { Game, GameSearchFilters, UserGame, GameStats } from '../types';
import type { SortOptions as GetRankedGamesSortOptions } from '../lib/game/searchService';
import GameFilters, { SortOption } from '../components/GameFilters';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getGameUrl } from '../lib/game/detailsService';
import SEO from '../components/SEO';

// Interface para a resposta paginada
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
  };
}

// Definir tipo local para o estado dos critérios de ranking
interface RankingCriteria {
  byRating: boolean;
  byOwnedCount: boolean;
  byWishlistCount: boolean;
  byPlayedCount: boolean;
  byFavoriteCount: boolean; // Manter se o checkbox existir, mesmo sem funcionalidade na query
}

const RankingPage: React.FC = () => {
  const { user } = useAuth();
  const [rankedGames, setRankedGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [userCollection, setUserCollection] = useState<Record<string, UserGame>>({});
  const [expandedGames, setExpandedGames] = useState<Record<string, boolean>>({});
  
  // Estado para armazenar a opção de ordenação no formato do GameFilters
  const [currentSortOption, setCurrentSortOption] = useState<SortOption>('rating_desc');
  
  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50; // 50 jogos por página conforme solicitado
  
  // Filtros
  const [filters, setFilters] = useState<GameSearchFilters>({
    categories: [],
    mechanics: [],
    themes: [],
    minPlayers: undefined,
    maxPlayers: undefined,
    maxPlayTime: undefined,
    minComplexity: undefined,
    maxComplexity: undefined,
    minAge: undefined,
  });

  // Usar o tipo local e renomear estado para clareza
  const [rankingCriteria, setRankingCriteria] = useState<RankingCriteria>({
    byRating: true,
    byOwnedCount: false,
    byWishlistCount: false,
    byPlayedCount: false,
    byFavoriteCount: false
  });

  // Carregar jogos da coleção do usuário quando o componente montar ou o usuário mudar
  useEffect(() => {
    const fetchUserCollection = async () => {
      if (!user?.id) return;
      
      try {
        // Buscar todos os jogos do usuário (coleção, wishlist, jogados)
        const { data, error } = await supabase
          .from('user_games')
          .select('*')
          .eq('user_id', user.id);
          
        if (error) {
          console.error('Erro ao buscar coleção do usuário:', error);
          return;
        }
        
        // Converter para um objeto com o game_id como chave para facilitar o acesso
        const userGamesMap: Record<string, UserGame> = {};
        data?.forEach(game => {
          // Garantir que play_count seja sempre um número
          userGamesMap[game.game_id] = { ...game, play_count: game.play_count || 0 } as UserGame;
        });
        
        console.log('Coleção do usuário carregada:', userGamesMap);
        setUserCollection(userGamesMap);
      } catch (error) {
        console.error('Erro ao processar coleção do usuário:', error);
      }
    };
    
    fetchUserCollection();
  }, [user]);

  const fetchRankedGames = useCallback(async () => {
    try {
      setLoading(true);
      
      // Determinar as opções de sort para getRankedGames
      let sortOptionsForQuery: GetRankedGamesSortOptions = {
        sortBy: 'average_rating', // Default
        ascending: false
      };

      // Configurar ordenação de acordo com os critérios selecionados
      if (rankingCriteria.byRating) {
        sortOptionsForQuery = {
          sortBy: 'average_rating',
          ascending: false
        };
        console.log("Ordenação inicial por nota média (será refinada no cliente)");
      }

      const response = await getRankedGames(
        sortOptionsForQuery,
        filters,
        currentPage,
        pageSize
      );
      
      // Atualizar estados de paginação
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.totalItems);
      
      // Obter os jogos básicos da resposta
      let games = response.data;
      
      console.log("Jogos ranqueados carregados:", games.length);
      
      // Adicionar um timestamp a todos os objetos stats para forçar atualização de cache
      const gamesWithTimestamp = games.map(game => {
        // Se o jogo já tem estatísticas no banco, vamos usá-las diretamente
        if (game.stats) {
          console.log(`Jogo ${game.name} já tem estatísticas armazenadas:`, JSON.stringify(game.stats, null, 2));
          return {
            ...game,
            stats: {
              ...game.stats,
              _timestamp: new Date().getTime()
            }
          };
        }
        
        // Se não tem stats, retornar o jogo sem modificações
        return game;
      });
      
      // Log para verificar a ordem final
      console.log("Ordem final dos jogos:", gamesWithTimestamp.map(g => 
        `${g.name}: owned=${g.stats?.owned_count}, wishlist=${g.stats?.wishlist_count}, played=${g.stats?.played_count}`
      ));
      
      // Aplicar ordenação local se necessário (para campos que não são ordenáveis diretamente no banco)
      let sortedGames = [...gamesWithTimestamp];
      
      if (rankingCriteria.byRating) {
        console.log("Ordenando jogos por avaliação considerando quantidade e nota");
        // Ordenação ponderada: Dá preferência aos jogos com maior quantidade de avaliações (70%)
        // mas ainda considera a nota média (30%). Isso permite que jogos populares e bem avaliados
        // apareçam primeiro, mesmo que não tenham a maior nota média absoluta.
        sortedGames = sortedGames.sort((a, b) => {
          const ratingCountA = Number(a.stats?.rating_count || 0);
          const ratingCountB = Number(b.stats?.rating_count || 0);
          const ratingA = Number(a.average_rating || 0);
          const ratingB = Number(b.average_rating || 0);
          
          // Calcular uma pontuação composta que considera tanto a quantidade como a nota
          // Usamos uma abordagem de ponderação: 70% para quantidade de avaliações, 30% para nota média
          // Primeiro, normalizar os valores entre 0 e 1
          const maxCount = Math.max(ratingCountA, ratingCountB, 1); // Evitar divisão por zero
          const normalizedCountA = ratingCountA / maxCount;
          const normalizedCountB = ratingCountB / maxCount;
          
          // Nota média já está normalizada (geralmente de 0 a 10 ou 0 a 5)
          const maxRating = 10; // Valor máximo estimado para normalização
          const normalizedRatingA = ratingA / maxRating;
          const normalizedRatingB = ratingB / maxRating;
          
          // Calcular pontuação final ponderada
          const scoreA = (normalizedCountA * 0.7) + (normalizedRatingA * 0.3);
          const scoreB = (normalizedCountB * 0.7) + (normalizedRatingB * 0.3);
          
          // Ordenar por pontuação decrescente
          return scoreB - scoreA;
        });
      } else if (rankingCriteria.byOwnedCount) {
        console.log("Ordenando jogos por quantidade de colecionadores");
        sortedGames = sortedGames.sort((a, b) => {
          const countA = Number(a.stats?.owned_count || 0);
          const countB = Number(b.stats?.owned_count || 0);
          console.log(`Comparando: ${a.name}(${countA}) com ${b.name}(${countB})`);
          return countB - countA; // Ordenação decrescente
        });
      } else if (rankingCriteria.byWishlistCount) {
        console.log("Ordenando jogos por quantidade na wishlist");
        sortedGames = sortedGames.sort((a, b) => {
          const countA = Number(a.stats?.wishlist_count || 0);
          const countB = Number(b.stats?.wishlist_count || 0);
          return countB - countA; // Ordenação decrescente
        });
      } else if (rankingCriteria.byPlayedCount) {
        console.log("Ordenando jogos por quantidade de jogadores");
        sortedGames = sortedGames.sort((a, b) => {
          const countA = Number(a.stats?.played_count || 0);
          const countB = Number(b.stats?.played_count || 0);
          return countB - countA; // Ordenação decrescente
        });
      }
      
      setRankedGames(sortedGames);
    } catch (error) {
      console.error('Erro ao buscar jogos ranqueados:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, rankingCriteria, currentPage, pageSize]);

  // Buscar jogos ranqueados quando os filtros, critérios de ranking ou página mudarem
  useEffect(() => {
    fetchRankedGames();
  }, [fetchRankedGames]);

  // Adicionar uma função para forçar atualização das estatísticas
  const forceRefreshStats = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Forçando atualização das estatísticas...");
      
      // Primeiro, executar a função que atualiza o campo stats para todos os jogos
      // Esta função irá chamar get_game_stats para cada jogo e atualizar o campo stats
      const { error: updateError } = await supabase.rpc('update_game_stats');
      
      if (updateError) {
        console.error("Erro ao atualizar estatísticas:", updateError);
        throw updateError;
      }
      
      // Aguardar um breve momento para garantir que as alterações sejam persistidas
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Agora recarregar os jogos com as estatísticas atualizadas
      await fetchRankedGames();
      
      console.log("Estatísticas atualizadas com sucesso!");
    } catch (error) {
      console.error("Erro ao forçar atualização das estatísticas:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchRankedGames]);

  const handleToggleOwned = useCallback(async (gameId: string, isOwned: boolean) => {
    if (!user?.id) return;
    
    try {
      const updatedUserGame = await toggleOwned(user.id, gameId, isOwned);
      
      setUserCollection(prev => {
        const newCollection = { ...prev };
        if (updatedUserGame) {
          newCollection[gameId] = {
            ...updatedUserGame,
            play_count: updatedUserGame.play_count || 0
          };
        } else {
          if (newCollection[gameId]) {
            newCollection[gameId] = {
              ...(newCollection[gameId] as UserGame),
              owned: false,
              play_count: (newCollection[gameId] as UserGame).play_count || 0
            };
          }
        }
        return newCollection;
      });
      
      // Forçar a atualização das estatísticas desse jogo
      console.log("Atualizando estatísticas após toggle Owned");
      const { data: stats, error } = await supabase.rpc('get_game_stats', { p_game_id: gameId });
      
      if (error) {
        console.error('Erro ao buscar estatísticas do jogo:', error);
        setRankedGames(prev => prev.map(g => g.id === gameId ? { ...g, isLoadingStats: false, stats: undefined } : g));
        return;
      }

      setRankedGames(prev => prev.map(g => g.id === gameId ? { ...g, stats: (stats as GameStats | null) || undefined, isLoadingStats: false } : g));
    } catch (error) {
      console.error('Erro ao atualizar coleção:', error);
    }
  }, [user?.id]);

  const handleToggleWishlist = useCallback(async (gameId: string, isWishlist: boolean) => {
    if (!user?.id) return;
    
    try {
      const updatedUserGame = await toggleWishlist(user.id, gameId, isWishlist);
      
      setUserCollection(prev => {
        const newCollection = { ...prev };
        if (updatedUserGame) {
          newCollection[gameId] = {
            ...updatedUserGame,
            play_count: updatedUserGame.play_count || 0
          };
        } else {
          if (newCollection[gameId]) {
            newCollection[gameId] = {
              ...(newCollection[gameId] as UserGame),
              wishlist: false,
              play_count: (newCollection[gameId] as UserGame).play_count || 0
            };
          }
        }
        return newCollection;
      });
      
      // Forçar a atualização das estatísticas desse jogo
      console.log("Atualizando estatísticas após toggle Wishlist");
      const { data: stats, error } = await supabase.rpc('get_game_stats', { p_game_id: gameId });
      
      if (error) {
        console.error('Erro ao buscar estatísticas do jogo (popularidade):', error);
        setRankedGames(prev => prev.map(g => g.id === gameId ? { ...g, isLoadingStats: false, stats: undefined } : g));
        return;
      }

      setRankedGames(prev => prev.map(g => g.id === gameId ? { ...g, stats: (stats as GameStats | null) || undefined, isLoadingStats: false } : g));
    } catch (error) {
      console.error('Erro ao atualizar wishlist:', error);
    }
  }, [user?.id]);

  const handleTogglePlayed = useCallback(async (gameId: string, isPlayed: boolean) => {
    if (!user?.id) return;
    
    try {
      const updatedUserGame = await togglePlayed(user.id, gameId, isPlayed);
      
      setUserCollection(prev => {
        const newCollection = { ...prev };
        if (updatedUserGame) {
          newCollection[gameId] = {
            ...updatedUserGame,
            play_count: updatedUserGame.play_count || 0
          };
        } else {
          if (newCollection[gameId]) {
            newCollection[gameId] = {
              ...(newCollection[gameId] as UserGame),
              play_count: 0
            };
          }
        }
        return newCollection;
      });
      
      // Forçar a atualização das estatísticas desse jogo
      console.log("Atualizando estatísticas após toggle Played");
      const { data: stats, error } = await supabase.rpc('get_game_stats', { p_game_id: gameId });
      
      if (error) {
        console.error('Erro ao buscar estatísticas do jogo (recentes):', error);
        setRankedGames(prev => prev.map(g => g.id === gameId ? { ...g, isLoadingStats: false, stats: undefined } : g));
        return;
      }

      setRankedGames(prev => prev.map(g => g.id === gameId ? { ...g, stats: (stats as GameStats | null) || undefined, isLoadingStats: false } : g));
    } catch (error) {
      console.error('Erro ao marcar jogo como jogado:', error);
    }
  }, [user?.id]);

  const handleFilterChange = useCallback((newFilters: GameSearchFilters) => {
    console.log('[RankingPage] handleFilterChange CHAMADO. Novos filtros:', newFilters);
    setFilters(newFilters);
    // Ao mudar os filtros, voltar para a primeira página
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((newSortOption: SortOption) => {
    console.log('[RankingPage] handleSortChange CHAMADO. Nova ordenação:', newSortOption);
    setCurrentSortOption(newSortOption);
    
    // Mapear a opção de ordenação para o formato de RankingCriteria
    let newRankingCriteria: RankingCriteria = {
      byRating: false,
      byOwnedCount: false,
      byWishlistCount: false,
      byPlayedCount: false,
      byFavoriteCount: false
    };
    
    switch (newSortOption) {
      case 'rating_desc':
        newRankingCriteria.byRating = true;
        break;
      case 'popularity_desc':
        newRankingCriteria.byOwnedCount = true;
        break;
      case 'name':
        // Quando ordenamos por nome, usamos a ordenação por avaliação por padrão no ranking
        newRankingCriteria.byRating = true;
        break;
      case 'year_desc':
        newRankingCriteria.byWishlistCount = true;
        break;
      case 'complexity_asc':
      case 'complexity_desc':
        newRankingCriteria.byPlayedCount = true;
        break;
      default:
        newRankingCriteria.byRating = true;
    }
    
    setRankingCriteria(newRankingCriteria);
    setCurrentPage(1); // Resetar página ao mudar ordenação
  }, []);

  // Função para converter RankingCriteria de volta para SortOption
  const getSortOptionFromRankingCriteria = useCallback((): SortOption => {
    if (rankingCriteria.byRating) return 'rating_desc';
    if (rankingCriteria.byOwnedCount) return 'popularity_desc';
    if (rankingCriteria.byWishlistCount) return 'year_desc';  
    if (rankingCriteria.byPlayedCount) return 'complexity_asc';
    return 'rating_desc'; // Padrão
  }, [rankingCriteria]);

  // clearFilters atualiza o estado de filtros e ordenação
  const clearFilters = useCallback(() => {
    setFilters({
      categories: [],
      mechanics: [],
      themes: [],
      minPlayers: undefined,
      maxPlayers: undefined,
      maxPlayTime: undefined,
      minComplexity: undefined,
      maxComplexity: undefined,
      minAge: undefined,
    });
    // Resetar para o estado inicial dos critérios
    setRankingCriteria({
      byRating: true,
      byOwnedCount: false,
      byWishlistCount: false,
      byPlayedCount: false,
      byFavoriteCount: false
    });
    // Também atualizar a opção de ordenação atual
    setCurrentSortOption('rating_desc');
    // Ao limpar os filtros, voltar para a primeira página
    setCurrentPage(1);
  }, []);

  // Função para alternar a expansão/colapso de um jogo
  const toggleGameExpansion = useCallback((gameId: string) => {
    setExpandedGames(prev => ({
      ...prev,
      [gameId]: !prev[gameId]
    }));
  }, []);
  
  // Manipuladores de paginação
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);
  
  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  // Componente para exibir as estatísticas de um jogo
  const GameStatsBadges = React.memo(({ game }: { game: Game }) => {
    const stats = game.stats;
    
    if (!stats) {
      console.log(`Sem estatísticas para o jogo ${game.name}`);
      return null;
    }
    
    // Log para depuração
    console.log(`Renderizando badges para ${game.name}:`, stats);
    
    // Garantir que todos os valores sejam números
    const ownedCount = Number(stats.owned_count || 0);
    const wishlistCount = Number(stats.wishlist_count || 0);
    const playedCount = Number(stats.played_count || 0);
    const playCount = Number(stats.play_count || 0);
    const ratingCount = Number(stats.rating_count || 0);
    
    return (
      <div className="flex flex-wrap gap-2 mt-2 text-xs">
        {ownedCount > 0 && (
          <div className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded flex items-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            <span>{ownedCount} {ownedCount === 1 ? 'pessoa possui' : 'pessoas possuem'}</span>
          </div>
        )}
        
        {wishlistCount > 0 && (
          <div className="bg-pink-50 text-pink-700 px-2 py-1 rounded flex items-center">
            <Heart className="w-3 h-3 mr-1" />
            <span>{wishlistCount} na wishlist</span>
          </div>
        )}
        
        {playedCount > 0 && (
          <div className="bg-teal-50 text-teal-700 px-2 py-1 rounded flex items-center">
            <PlusCircle className="w-3 h-3 mr-1" />
            <span>{playedCount} {playedCount === 1 ? 'pessoa jogou' : 'pessoas jogaram'}</span>
          </div>
        )}
        
        {playCount > 0 && (
          <div className="bg-green-50 text-green-700 px-2 py-1 rounded flex items-center">
            <PlusCircle className="w-3 h-3 mr-1" />
            <span>{playCount} jogadas</span>
          </div>
        )}
        
        {ratingCount > 0 && (
          <div className="bg-yellow-50 text-yellow-700 px-2 py-1 rounded flex items-center">
            <Star className="w-3 h-3 mr-1" />
            <span>{ratingCount} avaliações</span>
          </div>
        )}
      </div>
    );
  });

  return (
    <>
      <SEO 
        title="Ranking de Jogos"
        description="Confira os jogos de tabuleiro mais populares e bem avaliados pela comunidade."
      />
      <div className="container mx-auto max-w-6xl px-4 py-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">Ranking de Jogos</h1>
       
        <div className="bg-white dark:bg-gray-800 dark:border dark:border-gray-700 rounded-lg shadow mb-6 overflow-hidden">
          {/* Header com botões de ação */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center justify-between">
            <div className="flex items-center">
              <Award className="h-5 w-5 text-yellow-500 mr-2" />
              <span className="text-gray-700 font-medium dark:text-gray-200">
                {totalItems} jogos encontrados
              </span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-3 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition dark:bg-indigo-700/50 dark:text-indigo-300 dark:hover:bg-indigo-700/70"
              >
                {showFilters ? (
                  <>
                    <X className="h-4 w-4 mr-1" />
                    Fechar
                  </>
                ) : (
                  <>
                    <Filter className="h-4 w-4 mr-1" />
                    Filtros
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center px-3 py-2 text-sm bg-gray-50 text-gray-600 rounded-md hover:bg-gray-100 transition dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
          
          {/* Critérios de Ordenação */}
          <div className="p-4 border-b dark:border-gray-700">
            <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-2 text-sm">Ordenar por</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => handleSortChange('rating_desc')}
                className={`px-3 py-2 text-sm rounded-md flex items-center justify-center transition-colors ${
                  rankingCriteria.byRating 
                    ? 'bg-indigo-100 text-indigo-700 font-medium dark:bg-indigo-600 dark:text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <Star className="h-4 w-4 mr-1" />
                <span>Avaliação e Popularidade</span>
              </button>
              <button
                onClick={() => handleSortChange('popularity_desc')}
                className={`px-3 py-2 text-sm rounded-md flex items-center justify-center transition-colors ${
                  rankingCriteria.byOwnedCount 
                    ? 'bg-indigo-100 text-indigo-700 font-medium dark:bg-indigo-600 dark:text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                <span>Mais Colecionados</span>
              </button>
              <button
                onClick={() => handleSortChange('year_desc')}
                className={`px-3 py-2 text-sm rounded-md flex items-center justify-center transition-colors ${
                  rankingCriteria.byWishlistCount 
                    ? 'bg-indigo-100 text-indigo-700 font-medium dark:bg-indigo-600 dark:text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <Heart className="h-4 w-4 mr-1" />
                <span>Wishlists</span>
              </button>
              <button
                onClick={() => handleSortChange('complexity_asc')}
                className={`px-3 py-2 text-sm rounded-md flex items-center justify-center transition-colors ${
                  rankingCriteria.byPlayedCount 
                    ? 'bg-indigo-100 text-indigo-700 font-medium dark:bg-indigo-600 dark:text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                <PlusCircle className="h-4 w-4 mr-1" />
                <span>Pessoas que Jogaram</span>
              </button>
            </div>
          </div>
          
          {/* Filtros Expandidos */}
          {showFilters && (
            <div className="p-4 border-b border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700">
              <GameFilters 
                filters={filters} 
                onFilterChange={handleFilterChange}
                showFilters={true}
                sortBy={currentSortOption}
                setShowFilters={() => setShowFilters(false)}
                onSortChange={handleSortChange}
                onClearFilters={clearFilters}
                hideSortSelector={true}
              />
            </div>
          )}
        </div>
        
        {/* Resultados */}
        {loading ? (
          <div className="text-center p-12 bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="flex justify-center mb-4">
              <RefreshCw className="h-8 w-8 text-indigo-400 dark:text-indigo-500 animate-spin" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">Carregando jogos do ranking...</p>
          </div>
        ) : rankedGames.length === 0 ? (
          <div className="text-center p-12 bg-white dark:bg-gray-800 rounded-lg shadow">
            <p className="text-gray-500 dark:text-gray-400 mb-4">Não foram encontrados jogos com os filtros atuais.</p>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Limpar Filtros
            </button>
          </div>
        ) : (
          <>
            {/* Versão para desktop */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead className="bg-gray-50 dark:bg-gray-700 text-left">
                    <tr>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Posição</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Jogo</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Nota</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Detalhes</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estatísticas</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rankedGames.map((game, index) => (
                      <tr key={game.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        {/* Posição no ranking */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center justify-center bg-indigo-600 text-white w-8 h-8 rounded-full font-bold dark:bg-indigo-500">
                            {index + 1 + (currentPage - 1) * pageSize}
                          </div>
                        </td>
                        
                        {/* Informações do jogo */}
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="h-16 w-16 flex-shrink-0 mr-3">
                              <Link to={game.slug ? `/games/${game.slug}` : `/games/${game.id}`}>
                                <img 
                                  src={game.image_url || '/placeholder-game.webp'} 
                                  alt={game.name} 
                                  className="h-16 w-16 object-cover rounded"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/placeholder-game.webp';
                                  }}
                                />
                              </Link>
                            </div>
                            <div>
                              <Link 
                                to={game.slug ? `/games/${game.slug}` : `/games/${game.id}`}
                                className="text-sm font-medium text-gray-900 hover:text-indigo-600 dark:text-gray-100 dark:hover:text-indigo-400"
                              >
                                {game.name}
                              </Link>
                              {game.year && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{game.year}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        {/* Avaliação */}
                        <td className="px-4 py-3 text-center">
                          {game.average_rating ? (
                            <div className="flex flex-col items-center">
                              <div className="inline-flex items-center bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md dark:bg-yellow-700/30 dark:text-yellow-300">
                                <Star className="h-4 w-4 mr-1 text-yellow-500 fill-yellow-500 dark:text-yellow-400 dark:fill-yellow-400" />
                                <span className="font-medium">{game.average_rating.toFixed(1)}</span>
                              </div>
                              <div className="flex items-center text-xs text-gray-400 dark:text-gray-500 mt-1">
                                <span>{game.stats?.rating_count || 0}</span>
                                <ThumbsUp className="h-3 w-3 ml-0.5" />
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">Sem avaliação</span>
                          )}
                        </td>
                        
                        {/* Detalhes do jogo */}
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-600 dark:text-gray-300">
                            {game.min_players && game.max_players && (
                              <div className="flex items-center mb-1">
                                <Users className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                                <span>{game.min_players}-{game.max_players} jogadores</span>
                              </div>
                            )}
                            {game.play_time && (
                              <div className="flex items-center mb-1">
                                <Clock className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                                <span>{game.play_time} min</span>
                              </div>
                            )}
                            {game.categories && game.categories.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {game.categories.slice(0, 2).map((cat, i) => (
                                  <span key={i} className="text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full dark:bg-indigo-700/40 dark:text-indigo-300">{cat}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        
                        {/* Estatísticas do jogo */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col space-y-1">
                            {(game.stats?.owned_count || 0) > 0 && (
                              <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                                <CheckCircle className="w-3 h-3 mr-1 text-indigo-500 dark:text-indigo-400" />
                                <span>{game.stats?.owned_count} {(game.stats?.owned_count || 0) === 1 ? 'colecionador' : 'colecionadores'}</span>
                              </div>
                            )}
                            {(game.stats?.wishlist_count || 0) > 0 && (
                              <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                                <Heart className="w-3 h-3 mr-1 text-pink-500 dark:text-pink-400" />
                                <span>{game.stats?.wishlist_count} na wishlist</span>
                              </div>
                            )}
                            {(game.stats?.played_count || 0) > 0 && (
                              <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                                <PlusCircle className="w-3 h-3 mr-1 text-teal-500 dark:text-teal-400" />
                                <span>{game.stats?.played_count} {(game.stats?.played_count || 0) === 1 ? 'pessoa jogou' : 'pessoas jogaram'}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        
                        {/* Ações */}
                        <td className="px-4 py-3">
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => handleToggleOwned(game.id, !(userCollection[game.id]?.owned || false))}
                              className={`p-1.5 rounded transition-colors ${
                                userCollection[game.id]?.owned
                                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-600/70 dark:text-indigo-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                              title={userCollection[game.id]?.owned ? "Remover da coleção" : "Adicionar à coleção"}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={() => handleToggleWishlist(game.id, !(userCollection[game.id]?.wishlist || false))}
                              className={`p-1.5 rounded transition-colors ${
                                userCollection[game.id]?.wishlist
                                  ? 'bg-pink-100 text-pink-700 dark:bg-pink-600/70 dark:text-pink-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                              title={userCollection[game.id]?.wishlist ? "Remover da wishlist" : "Adicionar à wishlist"}
                            >
                              <Heart className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={() => {
                                const userGameEntry = userCollection[game.id];
                                handleTogglePlayed(game.id, !!(userGameEntry?.play_count && userGameEntry.play_count > 0));
                              }}
                              className={`p-1.5 rounded transition-colors ${
                                (userCollection[game.id]?.play_count || 0) > 0
                                  ? 'bg-green-100 text-green-700 dark:bg-green-600/70 dark:text-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                              title={(userCollection[game.id]?.play_count || 0) > 0 ? "Remover dos jogados" : "Marcar como jogado"}
                            >
                              <PlusCircle className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Versão para mobile - Cards adaptados */}
            <div className="md:hidden space-y-4">
              {rankedGames.map((game, index) => (
                <div key={game.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-start mb-3">
                      {/* Thumbnail e Info */}
                      <div className="flex-shrink-0 relative mr-3">
                        <div className="absolute -top-2 -left-2 flex items-center justify-center bg-indigo-600 text-white w-6 h-6 rounded-full font-bold text-xs z-10 dark:bg-indigo-500">
                          {index + 1 + (currentPage - 1) * pageSize}
                        </div>
                        <Link to={game.slug ? `/games/${game.slug}` : `/games/${game.id}`}>
                          <img 
                            src={game.image_url || '/placeholder-game.webp'} 
                            alt={game.name} 
                            className="w-16 h-16 object-cover rounded"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/placeholder-game.webp';
                            }}
                          />
                        </Link>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <Link 
                            to={game.slug ? `/games/${game.slug}` : `/games/${game.id}`}
                            className="font-medium text-gray-900 hover:text-indigo-600 dark:text-gray-100 dark:hover:text-indigo-400"
                          >
                            {game.name}
                          </Link>
                          
                          {game.average_rating && (
                            <div className="flex flex-col items-end ml-2">
                              <div className="inline-flex items-center bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-xs dark:bg-yellow-700/30 dark:text-yellow-300">
                                <Star className="h-3 w-3 mr-1 text-yellow-500 fill-yellow-500 dark:text-yellow-400 dark:fill-yellow-400" />
                                <span className="font-medium">{game.average_rating.toFixed(1)}</span>
                              </div>
                              <div className="flex items-center text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                <span>{game.stats?.rating_count || 0}</span>
                                <ThumbsUp className="h-3 w-3 ml-0.5" />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {game.year && <span>{game.year} · </span>}
                          {game.min_players && game.max_players && (
                            <span className="inline-flex items-center">
                              <Users className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                              {game.min_players}-{game.max_players} · 
                            </span>
                          )}
                          {game.play_time && (
                            <span className="inline-flex items-center ml-1">
                              <Clock className="h-3 w-3 mr-1 text-gray-400 dark:text-gray-500" />
                              {game.play_time} min
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Categorias */}
                    {game.categories && game.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2 mb-3">
                        {game.categories.slice(0, 3).map((cat, i) => (
                          <span key={i} className="text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full dark:bg-indigo-700/40 dark:text-indigo-300">{cat}</span>
                        ))}
                      </div>
                    )}
                    
                    {/* Estatísticas */}
                    <div className="flex flex-wrap gap-2 text-xs mb-3">
                      {(game.stats?.owned_count || 0) > 0 && (
                        <div className="inline-flex items-center bg-indigo-50 text-indigo-700 px-2 py-1 rounded dark:bg-indigo-700/40 dark:text-indigo-300">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          <span>{game.stats?.owned_count}</span>
                        </div>
                      )}
                      {(game.stats?.wishlist_count || 0) > 0 && (
                        <div className="inline-flex items-center bg-pink-50 text-pink-700 px-2 py-1 rounded dark:bg-pink-700/40 dark:text-pink-300">
                          <Heart className="w-3 h-3 mr-1" />
                          <span>{game.stats?.wishlist_count}</span>
                        </div>
                      )}
                      {(game.stats?.played_count || 0) > 0 && (
                        <div className="inline-flex items-center bg-teal-50 text-teal-700 px-2 py-1 rounded dark:bg-teal-700/40 dark:text-teal-300">
                          <PlusCircle className="w-3 h-3 mr-1" />
                          <span>{game.stats?.played_count}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Ações */}
                    <div className="flex justify-center space-x-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={() => handleToggleOwned(game.id, !(userCollection[game.id]?.owned || false))}
                        className={`p-2 rounded flex items-center transition-colors ${
                          userCollection[game.id]?.owned
                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-600/70 dark:text-indigo-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                        aria-label={userCollection[game.id]?.owned ? "Remover da coleção" : "Adicionar à coleção"}
                      >
                        <CheckCircle className="h-5 w-5 mr-1.5" />
                        <span className="text-sm">{userCollection[game.id]?.owned ? "Na coleção" : "Adicionar"}</span>
                      </button>
                      
                      <button
                        onClick={() => handleToggleWishlist(game.id, !(userCollection[game.id]?.wishlist || false))}
                        className={`p-2 rounded flex items-center transition-colors ${
                          userCollection[game.id]?.wishlist
                            ? 'bg-pink-100 text-pink-700 dark:bg-pink-600/70 dark:text-pink-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                        aria-label={userCollection[game.id]?.wishlist ? "Remover da wishlist" : "Adicionar à wishlist"}
                      >
                        <Heart className="h-5 w-5 mr-1.5" />
                        <span className="text-sm">{userCollection[game.id]?.wishlist ? "Wishlisted" : "Wishlist"}</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          const userGameEntry = userCollection[game.id];
                          handleTogglePlayed(game.id, !!(userGameEntry?.play_count && userGameEntry.play_count > 0));
                        }}
                        className={`p-2 rounded flex items-center transition-colors ${
                          (userCollection[game.id]?.play_count || 0) > 0
                            ? 'bg-green-100 text-green-700 dark:bg-green-600/70 dark:text-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                        aria-label={((userCollection[game.id]?.play_count || 0) > 0) ? "Remover dos jogados" : "Marcar como jogado"}
                      >
                        <PlusCircle className="h-5 w-5 mr-1.5" />
                        <span className="text-sm">{((userCollection[game.id]?.play_count || 0) > 0) ? "Jogado" : "Joguei"}</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Paginação - mantida igual */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage <= 1}
                  className={`flex items-center justify-center p-2 rounded-md ${
                    currentPage <= 1
                      ? 'text-gray-300 dark:text-gray-500 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                  className={`flex items-center justify-center p-2 rounded-md ${
                    currentPage >= totalPages
                      ? 'text-gray-300 dark:text-gray-500 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Exibindo {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalItems)} de {totalItems} jogos
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default RankingPage; 